// filepath: apps/web/src/editors/bpm-designer/BpmDesignerPage.tsx
//
// FR-UI-BLD-1 AC-3 / AC-4 - composition root for the BPM designer.
// Three-pane layout: palette (left) + canvas (center) + properties
// panel (right). Save button at top fires the save mutation; HTTP 422
// returns inline errors next to the offending node via the
// InlineNodeError component.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BpmNodePalette } from "./BpmNodePalette";
import { BpmCanvas } from "./BpmCanvas";
import { BpmPropertiesPanel } from "./BpmPropertiesPanel";
import {
  useFormList,
  useProcessSave,
  useRoleListForBpm,
  useScriptList,
} from "./queries";
import { classifyBpmDesignerError } from "./types";
import type {
  EdgeDefinition,
  NodeDefinition,
  NodeType,
  ProcessDraft,
  ValidationErrorWire,
} from "./types";

interface BpmDesignerPageProps {
  environmentKind: string;
}

const EMPTY_DRAFT: ProcessDraft = {
  name: "",
  description: "",
  nodes: [],
  edges: [],
};

function ensureNodeId(draft: ProcessDraft): string {
  let idx = draft.nodes.length + 1;
  while (draft.nodes.some((n) => n.node_id === `node_${idx}`)) {
    idx += 1;
  }
  return `node_${idx}`;
}

function ensureEdgeId(draft: ProcessDraft): string {
  let idx = draft.edges.length + 1;
  while (draft.edges.some((e) => e.edge_id === `edge_${idx}`)) {
    idx += 1;
  }
  return `edge_${idx}`;
}

export function BpmDesignerPage({
  environmentKind: _environmentKind,
}: BpmDesignerPageProps): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<ProcessDraft>(EMPTY_DRAFT);
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>();
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | undefined>();
  const [errors, setErrors] = useState<ValidationErrorWire[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  const formListQuery = useFormList();
  const roleListQuery = useRoleListForBpm();
  const scriptListQuery = useScriptList();
  const saveMutation = useProcessSave();

  function addNode(nodeType: NodeType): string {
    const id = ensureNodeId(draft);
    const node: NodeDefinition = { node_id: id, node_type: nodeType };
    setDraft((d) => ({ ...d, nodes: [...d.nodes, node] }));
    setSelectedNodeId(id);
    setSelectedEdgeId(undefined);
    return id;
  }

  function removeNode(nodeId: string): void {
    setDraft((d) => ({
      ...d,
      nodes: d.nodes.filter((n) => n.node_id !== nodeId),
      edges: d.edges.filter(
        (e) => e.source_node_id !== nodeId && e.target_node_id !== nodeId,
      ),
    }));
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(undefined);
    }
  }

  function updateNode(nodeId: string, next: NodeDefinition): void {
    setDraft((d) => ({
      ...d,
      nodes: d.nodes.map((n) => (n.node_id === nodeId ? next : n)),
    }));
  }

  function updateProcessMeta(
    next: Partial<Pick<ProcessDraft, "name" | "description">>,
  ): void {
    setDraft((d) => ({ ...d, ...next }));
  }

  function moveNode(nodeId: string, x: number, y: number): void {
    setDraft((d) => ({
      ...d,
      nodes: d.nodes.map((n) =>
        n.node_id === nodeId ? { ...n, label: n.label ?? n.node_type } : n,
      ),
    }));
    // x/y would normally be persisted on the node; for v1 the
    // visual position is local-only state.
    void x;
    void y;
    void nodeId;
  }

  function addEdge(sourceId: string, targetId: string): void {
    if (sourceId === targetId) return;
    const exists = draft.edges.some(
      (e) => e.source_node_id === sourceId && e.target_node_id === targetId,
    );
    if (exists) return;
    const edge: EdgeDefinition = {
      edge_id: ensureEdgeId(draft),
      source_node_id: sourceId,
      target_node_id: targetId,
    };
    setDraft((d) => ({ ...d, edges: [...d.edges, edge] }));
    setSelectedEdgeId(edge.edge_id);
    setSelectedNodeId(undefined);
  }

  function updateEdge(edgeId: string, next: Partial<EdgeDefinition>): void {
    setDraft((d) => ({
      ...d,
      edges: d.edges.map((e) => (e.edge_id === edgeId ? { ...e, ...next } : e)),
    }));
  }

  async function handleSave(): Promise<void> {
    setErrors([]);
    setSaveError(null);
    try {
      const response = await saveMutation.mutateAsync({
        name: draft.name,
        description: draft.description,
        nodes: draft.nodes,
        edges: draft.edges,
      });
      void navigate(`/admin/processes?new=${response.process_id}`);
    } catch (err) {
      const classified = classifySaveError(err);
      if (classified.kind === "validation_failed") {
        setErrors(classified.validationErrors ?? []);
      } else {
        setSaveError(classified.message);
      }
    }
  }

  return (
    <div className="bpm-designer-page" data-testid="bpm-designer-page">
      <header className="bpm-designer-page__header">
        <h1 className="bpm-designer-page__title">
          {t("admin.designer.page_title")}
        </h1>
        <button
          type="button"
          className="bpm-designer-page__save"
          onClick={() => void handleSave()}
          disabled={saveMutation.isPending}
          data-testid="bpm-designer-save"
        >
          {saveMutation.isPending
            ? t("admin.designer.save.saving")
            : t("admin.designer.save.cta")}
        </button>
      </header>
      {saveError && (
        <div
          role="alert"
          className="bpm-designer-page__save-error"
          data-testid="bpm-designer-save-error"
        >
          {saveError}
        </div>
      )}
      <div className="bpm-designer-page__layout">
        <BpmNodePalette onDragNodeType={(nt) => void nt} />
        <BpmCanvas
          nodes={draft.nodes}
          edges={draft.edges}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={selectedEdgeId}
          onSelectNode={setSelectedNodeId}
          onSelectEdge={setSelectedEdgeId}
          onRemoveNode={removeNode}
          onMoveNode={moveNode}
          onConnect={addEdge}
          onDropNodeType={(nt) => {
            addNode(nt as NodeType);
          }}
          errors={errors}
        />
        <BpmPropertiesPanel
          draft={draft}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={selectedEdgeId}
          onChangeSelectedNode={updateNode}
          onChangeSelectedEdge={updateEdge}
          onChangeProcessMeta={updateProcessMeta}
          forms={formListQuery.data ?? []}
          roles={roleListQuery.data ?? []}
          scripts={scriptListQuery.data ?? []}
          errors={errors}
        />
      </div>
    </div>
  );
}

function classifySaveError(
  err: unknown,
): ReturnType<typeof classifyBpmDesignerError> {
  const e = err as Error & { body?: unknown; status?: number };
  const status = typeof e?.status === "number" ? e.status : 500;
  const body = e?.body ?? e?.message ?? "";
  return classifyBpmDesignerError(status, body);
}
